/* =====================================================================
   SLIK BRAND THEME ENGINE — rebrand-from-one-hex
   BEHAVIOR-PIN: this is the multi-tenant productization seam. A business's
   entire look is a BrandTheme object. applyTheme() writes CSS custom
   properties on :root (and toggles body.photo-bw), which tailwind.config.js
   reads via rgb(var(--x) / <alpha-value>). Change one accent hex → the whole
   customer portal + landing page rebrand, no code, no rebuild.
   Warm Barbershop is theme #1 (SLIK). Next step: load per-tenant themes from
   Firestore businessSettings.colors instead of the constant below.
   See memory: project-slik-customer-portal-branding-2026-09-06.
===================================================================== */

export interface BrandTheme {
  /** Accent + primary CTA hex — drives copper/purple/indigo/violet/fuchsia. */
  primary: string;
  /** Page background hex (cream). */
  bg?: string;
  /** Dark surfaces / strong text hex (charcoal). */
  ink?: string;
  /** Body text hex. */
  text?: string;
  /** Photo treatment: 'bw' = editorial grayscale (default), 'color' = as-uploaded. */
  photo?: 'bw' | 'color';
}

/** SLIK's own brand — "Warm Barbershop" palette #1. */
export const SLIK_THEME: BrandTheme = {
  primary: '#B87333', // copper
  bg: '#F5EFE6',      // cream
  ink: '#1C1A17',     // charcoal
  text: '#2A211A',    // warm near-black
  photo: 'bw',
};

type RGB = [number, number, number];

const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

function hexToRgb(hex: string): RGB {
  const h = hex.replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const int = parseInt(full, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

const triplet = (rgb: RGB) => `${clamp(rgb[0])} ${clamp(rgb[1])} ${clamp(rgb[2])}`;

const mixWhite = (rgb: RGB, p: number): RGB =>
  [rgb[0] + (255 - rgb[0]) * p, rgb[1] + (255 - rgb[1]) * p, rgb[2] + (255 - rgb[2]) * p];

const mixBlack = (rgb: RGB, p: number): RGB =>
  [rgb[0] * (1 - p), rgb[1] * (1 - p), rgb[2] * (1 - p)];

/** Generate a Tailwind-style 50→900 ramp (as "R G B" triplets) from one base hex.
 *  500 = base; 50–400 tint toward white; 600–900 shade toward black. */
export function rampFromHex(hex: string): Record<string, string> {
  const base = hexToRgb(hex);
  return {
    '50': triplet(mixWhite(base, 0.92)),
    '100': triplet(mixWhite(base, 0.84)),
    '200': triplet(mixWhite(base, 0.66)),
    '300': triplet(mixWhite(base, 0.46)),
    '400': triplet(mixWhite(base, 0.24)),
    '500': triplet(base),
    '600': triplet(mixBlack(base, 0.18)),
    '700': triplet(mixBlack(base, 0.34)),
    '800': triplet(mixBlack(base, 0.50)),
    '900': triplet(mixBlack(base, 0.66)),
  };
}

/** Apply a BrandTheme to the live document (CSS vars + photo mode). */
export function applyTheme(theme: BrandTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Accent ramp drives copper + all remapped purple-family classes.
  const copper = rampFromHex(theme.primary);
  Object.entries(copper).forEach(([step, val]) => root.style.setProperty(`--copper-${step}`, val));

  if (theme.bg) root.style.setProperty('--brand-bg', triplet(hexToRgb(theme.bg)));
  if (theme.ink) root.style.setProperty('--brand-ink', triplet(hexToRgb(theme.ink)));
  if (theme.text) root.style.setProperty('--brand-text', triplet(hexToRgb(theme.text)));

  // Drive the mobile browser status-bar tint (was hardcoded purple) so it rebrands per tenant too.
  if (theme.bg) {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme.bg);
  }

  // Photo treatment toggle (editorial B&W is the default/SLIK look).
  document.body.classList.toggle('photo-bw', (theme.photo ?? 'bw') === 'bw');
}
